/**
 * Date utility functions using date-fns as replacement for moment.js
 * This file provides a centralized way to handle date operations
 */

import {
  format,
  parseISO,
  addMinutes,
  addSeconds,
  addMonths,
  addYears,
  subSeconds,
  isAfter,
  isBefore,
  fromUnixTime,
  getUnixTime,
  isValid as isValidDate,
} from "date-fns"

/**
 * Format date to string
 * @param {Date|string|number} date - Date to format
 * @param {string} formatString - Format pattern
 * @return {string} Formatted date string
 */
export const formatDate = (date, formatString = "MM/dd/yyyy") => {
  try {
    const dateObj = parseDate(date)
    if (!dateObj) return ""
    return format(dateObj, formatString)
  } catch (error) {
    console.warn("formatDate error:", error)
    return ""
  }
}

/**
 * Parse various date formats to Date object
 * @param {Date|string|number} date - Date to parse
 * @return {Date|null} Parsed date or null if invalid
 */
export const parseDate = date => {
  if (!date) return null

  if (date instanceof Date) {
    return isValidDate(date) ? date : null
  }

  if (typeof date === "number") {
    return fromUnixTime(date)
  }

  if (typeof date === "string") {
    // Try parsing ISO string first
    const isoDate = parseISO(date)
    if (isValidDate(isoDate)) return isoDate

    // Try parsing as regular date string
    const parsedDate = new Date(date)
    if (isValidDate(parsedDate)) return parsedDate
  }

  return null
}

/**
 * Add time to date
 * @param {Date|string} date - Base date
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit ('minutes', 'months', 'years', etc.)
 * @return {Date|null} New date with added time
 */
export const addToDate = (date, amount, unit) => {
  const dateObj = parseDate(date)
  if (!dateObj) return null

  switch (unit) {
    case "minutes":
    case "minute":
    case "m":
      return addMinutes(dateObj, amount)
    case "months":
    case "month":
    case "M":
      return addMonths(dateObj, amount)
    case "years":
    case "year":
    case "y":
      return addYears(dateObj, amount)
    case "seconds":
    case "second":
    case "s":
      return addSeconds(dateObj, amount)
    default:
      console.warn(`Unsupported unit: ${unit}`)
      return dateObj
  }
}

/**
 * Subtract time from date
 * @param {Date|string} date - Base date
 * @param {number} amount - Amount to subtract
 * @param {string} unit - Unit ('seconds', etc.)
 * @return {Date|null} New date with subtracted time
 */
export const subtractFromDate = (date, amount, unit) => {
  const dateObj = parseDate(date)
  if (!dateObj) return null

  switch (unit) {
    case "seconds":
    case "second":
    case "s":
      return subSeconds(dateObj, amount)
    default:
      // For other units, use negative addition
      return addToDate(date, -amount, unit)
  }
}

/**
 * Check if date1 is before date2
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @return {boolean} True if date1 is before date2
 */
export const isBeforeDate = (date1, date2) => {
  const d1 = parseDate(date1)
  const d2 = parseDate(date2)
  if (!d1 || !d2) return false
  return isBefore(d1, d2)
}

/**
 * Check if date1 is after date2
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @return {boolean} True if date1 is after date2
 */
export const isAfterDate = (date1, date2) => {
  const d1 = parseDate(date1)
  const d2 = parseDate(date2)
  if (!d1 || !d2) return false
  return isAfter(d1, d2)
}

/**
 * Get Unix timestamp from date
 * @param {Date|string} date - Date to convert
 * @return {number} Unix timestamp
 */
export const getUnixTimestamp = date => {
  const dateObj = parseDate(date)
  if (!dateObj) return 0
  return getUnixTime(dateObj)
}

/**
 * Create date from Unix timestamp
 * @param {number} timestamp - Unix timestamp
 * @return {Date} Date object
 */
export const fromUnixTimestamp = timestamp => {
  return fromUnixTime(timestamp)
}

/**
 * Check if date is valid
 * @param {any} date - Date to validate
 * @return {boolean} True if valid date
 */
export const isValidDateValue = date => {
  const dateObj = parseDate(date)
  return dateObj !== null && isValidDate(dateObj)
}

/**
 * Get current date
 * @return {Date} Current date
 */
export const getCurrentDate = () => {
  return new Date()
}

/**
 * Convert date to ISO string with timezone
 * @param {Date|string} date - Date to convert
 * @param {boolean} keepOffset - Whether to keep timezone offset
 * @return {string} ISO string
 */
export const toISOString = (date, keepOffset = false) => {
  const dateObj = parseDate(date)
  if (!dateObj) return ""

  if (keepOffset) {
    return dateObj.toISOString()
  }
  return dateObj.toISOString()
}

// Common date format patterns used in the codebase
export const DATE_FORMATS = {
  MM_DD_YYYY: "MM/dd/yyyy",
  YYYY_MM_DD: "yyyy-MM-dd",
  LL: "MMM d, yyyy", // Similar to moment's 'll' format
  LONG_DATE: "MMMM d, yyyy",
}

// Migration helpers - these functions provide moment.js-like API
export const momentCompatible = {
  /**
   * Moment-like constructor
   * @param {Date|string|number} date - Date to parse
   * @return {Object} Object with moment-like methods
   */
  moment: date => {
    const dateObj = parseDate(date) || getCurrentDate()

    return {
      format: formatString => formatDate(dateObj, formatString),
      add: (amount, unit) =>
        momentCompatible.moment(addToDate(dateObj, amount, unit)),
      subtract: (amount, unit) =>
        momentCompatible.moment(subtractFromDate(dateObj, amount, unit)),
      isBefore: otherDate => isBeforeDate(dateObj, otherDate),
      isAfter: otherDate => isAfterDate(dateObj, otherDate),
      unix: () => getUnixTimestamp(dateObj),
      toISOString: keepOffset => toISOString(dateObj, keepOffset),
      isValid: () => isValidDateValue(dateObj),
      toDate: () => dateObj,
    }
  },

  /**
   * Create moment from unix timestamp
   * @param {number} timestamp - Unix timestamp
   * @return {Object} Moment-like object
   */
  unix: timestamp => {
    return momentCompatible.moment(fromUnixTimestamp(timestamp))
  },
}
